import { Markdown } from '@/components/common/Markdown/Markdown';
import { CTA } from '@/components/sections/CTA/CTA';
import { Table } from '@/components/common/Table/Table';


const supportContent = `
# Support Policy

Effective date: Aug 31, 2024

This Support Policy and Service Level Agreement describes Cobalt's Support, Maintenance Services, and Service Levels which will be provided to customers of Cobalt purchasing an enterprise license to one of Cobalt's software solutions identified in a valid Order. Customer shall additionally receive Priority Support or Premium Support as designated in the applicable Order Form. All capitalized terms used herein shall have the meaning set forth in the Agreement, unless otherwise defined herein. During the relevant Subscription Term and subject to the terms of the Agreement entered into between the parties and any limitations stated herein, Cobalt will provide the following Support, Maintenance Services, and Service Levels to Customer:

## Standard Support Offering

Customer shall appoint up to three (3) individuals (or such other number identified in the relevant Order), at least one of whom has administrator privileges, who are knowledgeable in the operation of the Software to serve as the designated Customer contacts with Cobalt for support calls ("Designated Support Contacts"). Cobalt shall provide English-speaking remote assistance to Customer Contacts for questions or issues arising from any Error, as further described in this Policy, including troubleshooting, diagnosis, and recommendations for potential workarounds for the duration of Customer's subscription to the applicable Service. Cobalt shall also provide the specific entitlements as further described in this Policy and the tables below. Details for additional Support Subscriptions are described on the Cobalt Support Portal ("Cobalt Support Subscriptions"), which is incorporated herein by this reference.

- Slack
- Teams
- Dedicated Ticketing
- Direct personnel support via Infra Person + Engineer

## Submission of Support Tickets

Each Support Ticket shall: (a) designate the Severity Level of the Error in accordance with the definitions in ("Table 1 – Severity Levels For Confirmed Errors"), (b) identify the Customer Account that experienced the error, (c) include information sufficiently detailed to allow Cobalt Support to attempt to duplicate the Error (including any relevant error messages), and (d) provide contact information for the Customer Contact most familiar with the issue.

A "Confirmed Error" is defined as any failure of the Software to meet Cobalt's specifications for the Software outlined in the relevant Documentation. Unless Customer expressly designates the Severity Level, the Support Ticket will default to Severity Level 4. If Customer believes the issue to be related to Client Software (as defined in the Agreement), then the Support Ticket shall also include the applicable Client Software log files. If Customer Contacts submit Support Tickets related to enhancement or feature requests, Cobalt shall treat those tickets as closed once the request has been forwarded internally. Failure of Customer to respond to Cobalt's requests for a period of five (5) business days may result in Cobalt closing the Support Ticket. Customer may at any time add a new support case.

## Customer Responsibilities

Customer will provide Cobalt with reasonably requested information and assistance to help Cobalt identify, duplicate, and resolve the support issue, including where necessary, access to Customer's personnel, equipment, and testing environments as reasonably requested by Cobalt. Customer will maintain a current backup copy of all data processed or stored by the Software; Cobalt shall not be responsible for any loss of data. Customer will implement procedures for the protection of data and the implementation of backup facilities in the event of errors or malfunction of the Software. Customer will take all steps necessary to carry out procedures for the rectification of errors or malfunctions within a reasonable time after such procedures have been received from Cobalt. Customer will maintain a running instance of the Software in a non-production environment to enable and manage testing of Updates prior to release in a production environment and to enable troubleshooting in a non-production environment; failure to do so shall excuse Cobalt from meeting stated response times outlined in ("Table 2 – Support Response Times").

Customer shall have access to direct personnel support, including 1 Infra Person and 1 Engineer, to address Severity Level 1 issues. Customer shall nominate a Customer Contact with the required access privileges to work with Cobalt Support throughout the effort to resolve Severity Level 1 Support Tickets; failure to do so shall excuse Cobalt from meeting stated response times outlined in ("Table 2 – Support Response Times"). Cobalt's obligations to provide Priority Support are conditioned upon such Customer Contact being available to assist Cobalt Support with their resolution efforts. In such an event as the Customer Contact is unavailable, Cobalt will not be obligated to conform to the Cobalt Support Response Times outlined in Table 2 herein.

### Case Escalation
If Customer reasonably believes Cobalt Support is not performing in a professional manner or is failing to provide timely responses in accordance with this Policy, Customer may escalate the Support Ticket using the support escalation process described at the Cobalt Support Portal ("Case Escalation"). Any Support Ticket escalated by Customer will be directed to Cobalt's management team for consideration. Additional details on Priority Support are available on the Cobalt Support Portal ("Cobalt Support Subscriptions").

Customer shall have access to direct personnel support, including 1 Infra Person and 1 Engineer, to address Severity Level 1 and Severity Level 2 issues. Additional details on Priority Support are available on the Cobalt Support Portal ("Cobalt Support Subscriptions").

## Other Support and Training

Cobalt also offers various support and training resources such as documentation, community forums, FAQs, and user guides available on the Cobalt Support Portal and on Cobalt Community. Additionally, Cobalt offers for-fee consultation and training services via our Expert Services offerings.

## Error Severity Levels and Response Times

Cobalt's technical support offering includes the following response times which are dependent on the classification of the severity of the issues raised and are further based on the Customer's individually contracted Support Subscriptions. Upon receipt of a Support Ticket, Cobalt, at its sole discretion, determines the Error and assigns the applicable Severity Level based on descriptions in ("Table 1 – Severity Levels For Confirmed Errors"). Cobalt shall use commercially reasonable efforts to meet the Initial Response Time Target for the applicable Severity Level, as measured during In-Region Cobalt Support Hours set forth in ("Table 3 – Cobalt Support Hours") below (such hour(s), "Business Hour(s)" with the total Business Hours in an in-region support day being "Business Day(s)").

If the Customer Contact that submitted the Support Case is unresponsive or unreachable, Cobalt may downgrade the Severity Level by one level. If Cobalt's Severity Level designation is different from that assigned by Customer, Cobalt will promptly notify Customer of such designation change. If Customer notifies Cobalt of a reasonable basis for disagreeing with Cobalt's designated Severity Level, the parties will discuss in an effort to come to mutual agreement. If disagreement remains after discussion, each party will escalate within its organization and use good faith efforts to mutually agree on the appropriate Severity Level.

Additionally, for Customers purchasing a subscription to the Cobalt Cloud Solution, the Service Level Agreement below applies.`;

const supportContentPart2 = `
## Service Level Agreement

### Definitions

**Availability** – The Service is considered available if Customer is able to access and login to the Service. Additionally, the Service is considered available even if the user is unable to access or log into the service in the following cases:
- There is a scheduled downtime about which the customer was notified sufficiently in advance (at least 7 calendar days).
- The cluster is down as a result of an action by Customer.

**Monthly Availability Percentage** is defined as the difference between Calendar Minutes and the Unavailable Minutes, divided by Calendar Minutes, and multiplied by one hundred (100).

**Calendar Minutes** is defined as the total number of minutes in a given calendar month.

**Unavailable Minutes** is defined as the total accumulated minutes when the Service is Unavailable.

### Service Level

The Monthly "Uptime" Percentage for the Cobalt Service is ninety-nine and nine-tenths percent (99.9%) ("Service Level").

### Measurement and Reporting

Cobalt uses third-party tools to monitor Service Availability. The real-time availability status is reported using a status page located at https://status.cobalt.com/ (or such successor URL as may be designated by Cobalt).

## Cobalt Cloud Service Standard Data Backup Policy

Cobalt's Data Backup Policy applies to customers who have a valid subscription to Cobalt's Cloud Service and only to that subscription. Cobalt's "Standard" backup policy for Cobalt Cloud Production Instances includes:
- A daily backup of Metadata as stored in Cobalt Metadata database.
- Customer can request a full back-up on demand, not to exceed once a quarter. Cobalt reserves the right to deny such a request.

## Exclusions

Notwithstanding any of the foregoing, the following limitations apply: Cobalt is only responsible to provide Customer Designated Support Contacts with the Support and Maintenance Services described herein. The service levels described above apply only to Confirmed Errors found in the Software and only if Customer maintains a non-production environment where Updates are tested prior to use in the production environment. Cobalt shall not be responsible for:

- Third-Party Products, or any software, firmware, hardware not supplied by Cobalt, or for information or memory data contained in or stored on third-party products or services.
- Failure by Customer to maintain the proper operating system environment or to use the Software in accordance with the Agreement or the Documentation.
- Software that is modified other than by Cobalt or its personnel.
- Software that is marked End of Life as per Cobalt Release Support Policy located at https://docs.cobalt.com/home/cobalt-release-support-policy/ (or such successor URL as may be designated by Cobalt).
- Evaluation Versions of the Software or other software provided at no charge.
- Training, customization, integration, and any issues arising from unlicensed use of the Software.
- Any on-site services or remote access services. Support services do not include on-site support, consulting (re-designing, re-programming or reconfiguring the Customer's Software or Implementation), and system design, program coding, project management, facility management, or support for incompatible products or third-party suppliers' products.

Services requested outside of the scope of this policy may be reviewed as for-fee additional work delivered as part of our Expert Services offerings.

## Change

Cobalt may make changes to this policy with thirty (30) days' notice to Customer (via the support portal or otherwise), provided such change is in connection with a standard change made to its then-current standard Support Policy and there is no material degradation to the support offering.`;



const Support = () => {

    const severityTableTitle = "Table 1: Severity level and types";
  const severityHeaders = [
    { header: "Severity Level", key: "level" },
    { header: "Error Description", key: "description" }
  ];
  const severityRows = [
    {
      level: "Severity Level 1 (Ticket Priority: Sev 1)",
      description: "Error rendering the Software completely inoperable or down, or critical subsystem in Software is down with critical impact on Customer's business. No workaround or alternative is available."
    },
    {
      level: "Severity Level 2 (Ticket Priority: Sev 2)",
      description: "Error rendering a) the Software performance is substantially degraded or restricted in production environment; b) system and/or data is exposed to potential loss, interruption; c) the Software completely inoperable or down or critical subsystem in Software is down on Customer's staging system/environment during testing, causing a significant impact on Customer's business. No bypass or alternative is available."
    },
    {
      level: "Severity Level 3 (Ticket Priority: Sev 3)",
      description: "Error that has a medium-to-low impact on Software operability or functionality (with or without a workaround) in Customer's production or staging environment."
    },
    {
      level: "Severity Level 4 (Ticket Priority: Sev 4)",
      description: "a) An Error that has low-to-no impact on Software operability or functionality (with or without a workaround) in Customer's production or staging environment;\nb) General questions regarding Software functionality;\nc) Minor imperfections, discrepancy or peculiarity in Software or Documentation causing low or no impact on Customer's business.\nEnhancement and Feature Requests are not included in this severity level but may be proposed to Cobalt Support."
    }
  ];

     // Table 2 data: Response SLAs
  const slaTableTitle = "Table 2: Response SLAs";
  const slaRows: [string, string, string][] = [
    ["P1 Response", "8 hours, 24×7", "1 hour, 24×7"],
    ["P1 Update Frequency***", "8 hours, 24×5", "2 hour, 24×7"],
    ["P2 Response", "8 business hours", "4 hours, 24×5"],
    ["P2 Update Frequency***", "1 business day", "8 hours, 24×5"],
    ["P3 Response", "1 business day", "4 business hours"],
    ["P3 Update Frequency", "5 business days", "1 business day"],
    ["P4 Response", "2 business days", "1 business day"],
    ["P4 Update Frequency", "20 business days", "5 business days"]
  ];
  const slaFootnote = "*** Update frequency times apply until the system is back up or a workaround is identified.";
  const slaColumns = [
    { header: "Plan Benefits", key: "benefit" },
    { header: "Standard Support", key: "standard" },
    { header: "Premium Support", key: "premium" }
  ];
  const slaData = slaRows.map((row: [string, string, string]) => ({
    benefit: row[0],
    standard: row[1],
    premium: row[2]
  }));

  // Table 3 data: Support Hours by Region
  const regionRows: [string, string, string, string][] = [
    ["Support Business Hours", "Monday through Friday 8AM to 8PM EST/PST", "Monday through Friday 8AM to 5PM GMT", "Monday through Friday 8AM to 5PM SGT"],
    ["Excluded Holidays", "Recognized US Federal Holidays", "Recognized EMEA Bank Holidays", "Recognized APAC Holidays"]
  ];
  const regionColumns = [
    { header: "Cobalt Service Region", key: "region" },
    { header: "Americas", key: "americas" },
    { header: "Europe, Middle East, and Africa", key: "emea" },
    { header: "Asia Pacific", key: "apac" }
  ];
  const regionData = regionRows.map((row: [string, string, string, string]) => ({
    region: row[0],
    americas: row[1],
    emea: row[2],
    apac: row[3]
  }));


  return (
    <div className="min-h-screen bg-white">
      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <Markdown content={supportContent} />
          <Table
            title={severityTableTitle}
            columns={severityHeaders}
            data={severityRows}
          />

          <Table
            title={slaTableTitle}
            columns={slaColumns}
            data={slaData}
          />
          {slaFootnote && (
            <p className="mt-2 text-sm text-gray-600 italic">{slaFootnote}</p>
          )}

          <div className="mt-8 mb-4">
            <h2 className="text-2xl font-bold">Support Hours</h2>
            <p className="my-4">
              Cobalt assigns the ticket's service region based on the designated Contact's location. The customer may request a change of the
              designated service region by emailing support@cobalt.com with the request and the required designation. The change is limited to
              twice a year for each Contact.
            </p>
          </div>

          <Table

            columns={regionColumns}
            data={regionData}
          />

         <div className="mt-8 mb-4">
            <h2 className="text-2xl font-bold">Maintenance</h2>
            <p className="my-4 mb-12">
              As part of the subscription license to Software and as governed by the Release Support Policy located at https://docs.cobalt.com/home/cobalt-release-support-policy/ (or such successor URL as may be designated by Cobalt), Cobalt will provide Customer, free of charge, with access to all bug fixes, patches, upgrades, new features, or functional enhancements to the Software that are generally made available free of charge to Cobalt’s customers who have purchased a subscription to Software (“Updates”).
            </p>
          </div>

          <Markdown content={supportContentPart2} />
        </div>
      </div>

      {/* CTA Section */}
      <CTA
        title="Need help with our support services?"
        description="Our team is here to help you with any questions about our support policy and services."
        cta={{
          text: "Contact Support",
          href: "/contact",
          secondary: {
            text: "View Documentation",
            href: "/docs"
          }
        }}
      />
    </div>
  );
};

export default Support;